<template>
    <div class="dashboard-wrapper">
        <b-row class="mt-3 stat-cards-row">
            <b-col cols="12" sm="6" md="6" lg="3" v-for="card in statCards" :key="card.key">
                <div
                    class="alert-card shadow-sm p-2 mb-3"
                    :class="card.cardClass"
                >
                    <div class="card-content">
                        <div class="icon-box" :class="card.iconClass">
                            <i :class="card.icon"></i>
                        </div>
                        <div class="card-text">
                            <div class="mb-1 main-title">{{ card.label }}</div>
                            <h5 class="m-0 font-weight-bold">
                                {{ dashboardStats[card.key] }}
                            </h5>
                        </div>
                    </div>
                </div>
            </b-col>
        </b-row>

        <b-card class="search-card shadow-s">
            <b-row class="align-items-center search-filters-row">
                <b-col cols="12" md="8" class="search-col">
                    <div class="search-wrapper">
                        <i class="fa fa-search search-icon"></i>
                        <b-form-input
                            v-model="search"
                            class="search-input"
                            placeholder="Search by alert ID, user, or alert type..."
                            @input="resetPage"
                        />
                    </div>
                </b-col>

                <b-col cols="6" md="2" class="filter-col">
                    <b-form-select
                        v-model="selectedFilter1"
                        :options="filterOptions"
                        class="filter-select"
                        @change="resetPage"
                    />
                </b-col>

                <b-col cols="6" md="2" class="filter-col">
                    <b-form-select
                        v-model="selectedFilter2"
                        :options="filterOptions2"
                        class="filter-select"
                        @change="resetPage"
                    />
                </b-col>
            </b-row>
        </b-card>

        <div class="alert-list">
            <FraudAlertCard
                v-for="alert in paginatedFraudAlerts"
                :key="alert.id"
                :alert="alert"
                @user-blocked="handleUserBlocked"
            />
        </div>

        <div class="pagination-wrapper" v-if="totalPages > 1">
            <b-pagination
                v-model="currentPage"
                :total-rows="totalFilteredAlerts"
                :per-page="itemsPerPage"
                aria-controls="my-table"
                prev-text="Prev"
                next-text="Next"
                hide-goto-end-buttons
                class="justify-content-center justify-content-md-end"
                @change="onPageChange"
            ></b-pagination>
        </div>
    </div>
</template>
<script>
import FraudAlertCard from "@/views/fraudManagement/FraudAlertCard.vue";
import { mapState } from "vuex";
import moment from "moment";

export default {
    components: { FraudAlertCard },

    data() {
        return {
            dashboardStats: {
                totalAlerts: 0,
                criticalAlerts: 0,
                activeAlerts: 0,
                investigationAlerts: 0,
            },
            search: "",
            selectedFilter1: "",
            selectedFilter2: "",
            currentPage: 1,
            itemsPerPage: 15,
            filterOptions: [
                { value: "", text: "All" },
                { value: "critical", text: "Critical" },
                { value: "active", text: "Active" },
                { value: "investigation", text: "Under Investigation" },
            ],
            filterOptions2: [
                { value: "", text: "All" },
                { value: "critical", text: "Critical" },
                { value: "high", text: "High" },
                { value: "medium", text: "Medium" },
                { value: "low", text: "Low" },
            ],
        };
    },

    computed: {
        ...mapState({
            fraudAlertListData: (s) => s.fraudManagement.fraudAlertList,
            blockUserListData: (s) => s.fraudManagement.blockUserList,
        }),

        fraudAlertList() {
            return this.fraudAlertListData?.data?.result || [];
        },

        blockedCustomerIds() {
            const raw = this.blockUserListData;
            const list = Array.isArray(raw)
                ? raw
                : raw?.response && Array.isArray(raw.response)
                  ? raw.response
                  : [];
            return new Set(list.map((item) => item._id).filter(Boolean));
        },

        statCards() {
            return [
                {
                    key: "totalAlerts",
                    label: "Total Alerts",
                    icon: "fa fa-bell",
                    cardClass: "total-card",
                    iconClass: "total-icon",
                },
                {
                    key: "criticalAlerts",
                    label: "Critical Alerts",
                    icon: "fa fa-exclamation-triangle",
                    cardClass: "critical-card",
                    iconClass: "critical-icon",
                },
                {
                    key: "activeAlerts",
                    label: "Active Alerts",
                    icon: "fa fa-shield",
                    cardClass: "active-card",
                    iconClass: "active-icon",
                },
                {
                    key: "investigationAlerts",
                    label: "Under Investigation",
                    icon: "fa fa-clock-o",
                    cardClass: "investigation-card",
                    iconClass: "investigation-icon",
                },
            ];
        },

        fraudAlerts() {
            return this.fraudAlertList.map((item) => {
                const riskMap = {
                    critical: {
                        variant: "danger",
                        score: 95,
                        border: "#ffcccc",
                    },
                    high: { variant: "warning", score: 85, border: "#ffddaa" },
                    medium: { variant: "info", score: 60, border: "#cce5ff" },
                    low: { variant: "secondary", score: 30, border: "#e0e0e0" },
                };

                const riskKey = item.risk?.toLowerCase() || "medium";
                const risk = riskMap[riskKey] || riskMap.medium;

                const title = item.reason
                    ? item.reason
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())
                    : item.event_status || "Fraud Alert";

                const message =
                    [
                        item.orderamount && !isNaN(item.orderamount)
                            ? `Order amount: $${parseFloat(item.orderamount).toFixed(2)}`
                            : null,
                        item.orderstatus ? `Status: ${item.orderstatus}` : null,
                        item.reason
                            ? `Reason: ${item.reason.replace(/_/g, " ")}`
                            : null,
                    ]
                        .filter(Boolean)
                        .join(" | ") || "Fraud alert detected";

                return {
                    id: `ALT-${item.id}`,
                    severity: item.risk || "Medium",
                    severityVariant: risk.variant,
                    borderColor: risk.border,
                    title,
                    user: item.customer_name || "NA",
                    message,
                    time: this.formatDate(item.createdat),
                    createdAt: item.createdat || null,
                    risk: risk.score,
                    orders: item.order_id
                        ? [item.order_id]
                        : item.id
                          ? [`ORD-${item.id}`]
                          : [],
                    customer_id: item.customer_id,
                    fraud_source: item.fraud_source,
                    isBlocked: this.blockedCustomerIds.has(item.customer_id),
                };
            });
        },

        filteredFraudAlerts() {
            const q = this.search.toLowerCase();

            const filtered = this.fraudAlerts.filter((a) => {
                const matchSearch =
                    !q ||
                    [a.id, a.user, a.title, a.message].some((v) =>
                        v.toLowerCase().includes(q),
                    );

                const matchFilter1 =
                    !this.selectedFilter1 ||
                    (this.selectedFilter1 === "critical" &&
                        a.severity.toLowerCase() === "critical") ||
                    (this.selectedFilter1 === "active" &&
                        a.severityVariant !== "secondary" &&
                        a.risk >= 60) ||
                    (this.selectedFilter1 === "investigation" && a.risk >= 85);

                const matchFilter2 =
                    !this.selectedFilter2 ||
                    a.severity.toLowerCase() ===
                        this.selectedFilter2.toLowerCase();

                return matchSearch && matchFilter1 && matchFilter2;
            });

            return filtered.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return dateB - dateA;
            });
        },

        totalFilteredAlerts() {
            return this.filteredFraudAlerts.length;
        },

        totalPages() {
            return Math.ceil(this.totalFilteredAlerts / this.itemsPerPage);
        },

        paginatedFraudAlerts() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredFraudAlerts.slice(start, end);
        },
    },

    methods: {
        formatDate(date) {
            if (!date) return "N/A";
            return new Date(date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        },

        resetPage() {
            this.currentPage = 1;
        },

        onPageChange(page) {
            this.currentPage = page;
            this.$nextTick(() => {
                const alertList = document.querySelector(".alert-list");
                if (alertList) {
                    alertList.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            });
        },

        async getFraudManagementData() {
            const payload = {
                tablename: "fraud_report",
                fromDate: moment().subtract(1, "year").format("YYYY-MM-DD"),
                toDate: moment().format("YYYY-MM-DD"),
            };

            await this.$store.dispatch(
                "fraudManagement/fetchFraudAlertList",
                payload,
            );

            this.dashboardStats.totalAlerts =
                this.fraudAlertListData?.data?.parameters?.pagination
                    ?.totalItems || 0;
        },
        async getFraudAlertData() {
            const payload = {
                items: [
                    {
                        key: "total-alert-risk",
                        variables: {
                            fromDate: moment()
                                .subtract(1, "year")
                                .format("YYYY-MM-DD"),
                            toDate: moment().format("YYYY-MM-DD"),
                            tenant: this.$currentUserData.tenant,
                        },
                    },
                    {
                        key: "total-active-alert",
                        variables: {
                            fromDate: moment()
                                .subtract(1, "year")
                                .format("YYYY-MM-DD"),
                            toDate: moment().format("YYYY-MM-DD"),
                            tenant: this.$currentUserData.tenant,
                        },
                    },
                    {
                        key: "total-investigate-alert",
                        variables: {
                            fromDate: moment()
                                .subtract(1, "year")
                                .format("YYYY-MM-DD"),
                            toDate: moment().format("YYYY-MM-DD"),
                            tenant: this.$currentUserData.tenant,
                        },
                    },
                ],
            };
            const res = await this.$store.dispatch(
                "partner/fetchFraudAlertData",
                payload,
            );
            this.dashboardStats.criticalAlerts =
                res["total-alert-risk"]?.critical_alerts || 0;
            this.dashboardStats.activeAlerts =
                res["total-active-alert"]?.critical_alerts || 0;
            this.dashboardStats.investigationAlerts =
                res["total-investigate-alert"]?.critical_alerts || 0;
        },

        async handleUserBlocked() {
            await this.getFraudManagementData();
            await this.fetchBlockUserList();
        },
        async fetchBlockUserList() {
            await this.$store.dispatch("fraudManagement/fetchBlockUserList");
        },
    },

    mounted() {
        this.getFraudAlertData();
        this.getFraudManagementData();
        this.fetchBlockUserList();
    },
};
</script>
<style scoped>
.dashboard-wrapper {
    padding: 0.5rem;
    max-width: 100%;
    overflow-x: hidden;
}

/* Stat cards - responsive grid */

.stat-cards-row .alert-card {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
}

.alert-card {
    min-height: 80px;
    height: auto;
    border-radius: 10px;
    background: #fff;
    display: flex;
    align-items: center;
}

.card-content {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
}

.card-text {
    margin-left: 0.75rem;
    min-width: 0;
    flex: 1;
}

.main-title {
    font-size: clamp(12px, 2.5vw, 15px);
    font-weight: 500;
    color: #222;
}

.icon-box {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

@media (min-width: 576px) {
    .icon-box {
        width: 38px;
        height: 38px;
        min-width: 38px;
        font-size: 18px;
    }
}

.total-icon {
    background: #d9eaff;
    color: #4a8ef0;
}
.critical-icon {
    background: #ffdad9;
    color: #e63939;
}
.active-icon {
    background: #ffe7c3;
    color: #ff9e1f;
}
.investigation-icon {
    background: #fff3b6;
    color: #d4a733;
}

.search-card {
    border-radius: 14px;
    border: none;
}

.search-filters-row {
    row-gap: 0.75rem;
}

.search-col {
    order: 1;
}

.filter-col {
    order: 2;
}

.search-wrapper {
    position: relative;
    width: 100%;
}

.search-input {
    padding-left: 38px !important;
    height: 42px !important;
    border-radius: 10px;
    width: 100%;
    max-width: 100%;
}

.search-icon {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    z-index: 1;
    pointer-events: none;
}

.filter-select {
    height: 42px;
    border-radius: 10px !important;
    width: 100%;
    max-width: 100%;
}

.alert-list {
    min-height: 200px;
}

.pagination-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 1rem;
    padding: 0 0.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.pagination-wrapper::v-deep .pagination {
    flex-wrap: wrap;
    justify-content: center;
}

@media (min-width: 768px) {
    .dashboard-wrapper {
        padding: 1rem;
    }

    .pagination-wrapper {
        justify-content: flex-end;
    }

    .search-filters-row {
        row-gap: 0;
    }
}

@media (max-width: 575.98px) {
    .stat-cards-row .alert-card {
        margin-bottom: 0.75rem !important;
    }

    .search-col {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }

    .filter-col {
        padding-left: 0.25rem;
        padding-right: 0.25rem;
    }
}

@media (max-width: 767.98px) {
    .alert-card {
        padding: 1rem !important;
    }

    .card-text h5 {
        font-size: 1rem;
    }
}

@media (min-width: 1200px) {
    .dashboard-wrapper {
        padding: 1.25rem;
    }
}
</style>
